import { useEffect, useRef } from 'react';
import { useI18n, useTheme } from '@xdocs/ui';
import { useWs } from './WsProvider.js';

/**
 * Best-effort mirrors the locally-persisted theme/locale (see
 * `ThemeController`/`LocaleController` — localStorage is the source of
 * truth for THIS browser) to `users.theme`/`users.locale` via
 * `settings.setTheme`/`settings.setLocale` once authenticated, so a
 * different device picks up the same preference. Mount ONLY inside the
 * authenticated shell (`AppShellLayout`) — non-critical enrichment
 * (platform.md: fail-open), never blocks rendering, never surfaces an
 * error to the user on failure.
 */
export function useSyncSettingsToServer(): void {
  const { wsClient, connectionState } = useWs();
  const { theme } = useTheme();
  const { locale } = useI18n();
  const lastSynced = useRef<{ theme: string; locale: string } | null>(null);

  useEffect(() => {
    if (connectionState !== 'connected') return;
    if (lastSynced.current?.theme === theme && lastSynced.current?.locale === locale) return;
    const snapshot = { theme, locale };
    lastSynced.current = snapshot;

    void wsClient.request('settings.setTheme', { theme }).catch(() => {
      // Fail-open: non-critical enrichment, localStorage already has it.
    });
    void wsClient.request('settings.setLocale', { locale }).catch(() => {
      // Fail-open: non-critical enrichment, localStorage already has it.
    });
  }, [wsClient, connectionState, theme, locale]);
}
